import { Button, Modal } from '@braskai/ui-kit';

import { alpha, css, keyframes, styled } from '@mui/material';

const REDUCED_MOTION_QUERY = '@media (prefers-reduced-motion: reduce)';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: none; }
`;

const pinNudge = keyframes`
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  15% { transform: translate(0, -2px) rotate(-8deg); }
  30% { transform: translate(0, 0) rotate(0deg); }
`;

export const SDialog = styled(Modal)(
  ({ theme }) => css`
    .MuiBackdrop-root {
      background-color: ${alpha(theme.palette.grey[900], 0.45)};
      backdrop-filter: blur(3px);
    }

    .MuiDialog-paper {
      width: 100%;
      max-width: 440px;
      border-radius: 20px;
      background-color: transparent;
      box-shadow:
        0 24px 64px -12px ${alpha(theme.palette.grey[900], 0.28)},
        0 0 0 1px ${alpha(theme.palette.grey[900], 0.04)};
    }
  `,
);

export const SCard = styled('div')(
  ({ theme }) => css`
    position: relative;
    padding: 28px;
    border-radius: 20px;
    background-color: ${theme.palette.common.white};
    color: ${theme.palette.grey[900]};
    font-family: ${theme.typography.fontFamily};
    animation: ${fadeUp} 0.22s ease;

    ${REDUCED_MOTION_QUERY} {
      animation: none;
    }
  `,
);

export const SCloseButton = styled('button')(
  ({ theme }) => css`
    position: absolute;
    top: 18px;
    right: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    color: ${theme.palette.grey[400]};
    background: transparent;
    cursor: pointer;

    &:hover {
      color: ${theme.palette.grey[700]};
      background-color: ${theme.palette.grey[100]};
    }
  `,
);

export const STitle = styled('h3')(
  ({ theme }) => css`
    margin: 24px 0 0;
    padding-right: 8px;
    color: ${theme.palette.grey[900]};
    font-size: 22px;
    font-weight: 600;
    line-height: 28px;
    letter-spacing: -0.015em;
    text-wrap: pretty;
  `,
);

export const SPreview = styled('div', {
  shouldForwardProp: (prop) => prop !== '$height' && prop !== '$marginBottom',
})<{ $height: number; $marginBottom: number }>(
  ({ theme, $height, $marginBottom }) => css`
    position: relative;
    height: ${$height}px;
    margin: 16px 0 ${$marginBottom}px;
    overflow: hidden;
    border: 1px solid ${theme.palette.grey[200]};
    border-radius: 14px;
    background: ${theme.palette.grey[50]};
  `,
);

export const SBrowserBar = styled('div')(
  ({ theme }) => css`
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    height: 34px;
    padding: 0 12px;
    border-bottom: 1px solid ${theme.palette.grey[200]};
    background: ${theme.palette.common.white};
  `,
);

export const SBrowserDots = styled('span')(
  ({ theme }) => css`
    display: flex;
    gap: 5px;

    & > span {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: ${theme.palette.grey[300]};
    }
  `,
);

export const SAddressBar = styled('span')(
  ({ theme }) => css`
    flex: 1;
    height: 18px;
    border-radius: 999px;
    background: ${theme.palette.grey[100]};
  `,
);

export const SToolbarLogo = styled('span')(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    background: ${theme.palette.primary[50]};
    color: ${theme.palette.primary[600]};
    box-shadow:
      0 0 0 2px ${theme.palette.common.white},
      0 0 0 3px ${theme.palette.primary[200]};
  `,
);

export const SStoreCard = styled('div')(
  ({ theme }) => css`
    position: absolute;
    top: 58px;
    left: 50%;
    display: flex;
    align-items: center;
    gap: 12px;
    /* 330px like the prototype, growing just enough to keep the full extension name readable. */
    width: max-content;
    min-width: min(330px, calc(100% - 24px));
    max-width: calc(100% - 24px);
    box-sizing: border-box;
    padding: 14px;
    border: 1px solid ${theme.palette.grey[200]};
    border-radius: 14px;
    background: ${theme.palette.common.white};
    box-shadow: 0 14px 32px -16px ${alpha(theme.palette.grey[900], 0.3)};
    transform: translateX(-50%);
  `,
);

export const SStoreLogo = styled('span')(
  ({ theme }) => css`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: ${theme.palette.primary[50]};
    color: ${theme.palette.primary[600]};
  `,
);

export const SStoreText = styled('span')`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

export const SStoreName = styled('span')(
  ({ theme }) => css`
    overflow: hidden;
    color: ${theme.palette.grey[900]};
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
);

export const SStoreKind = styled('span')(
  ({ theme }) => css`
    color: ${theme.palette.grey[500]};
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
  `,
);

export const SStorePill = styled('span')(
  ({ theme }) => css`
    display: inline-flex;
    align-items: center;
    height: 28px;
    padding: 0 12px;
    border-radius: 999px;
    background: ${theme.palette.primary[600]};
    color: ${theme.palette.common.white};
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
  `,
);

export const SToolbar = styled('div')(
  ({ theme }) => css`
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    height: 38px;
    padding: 0 16px;
    border-bottom: 1px solid ${theme.palette.grey[200]};
    background: ${theme.palette.common.white};
    color: ${theme.palette.grey[400]};
  `,
);

export const SPuzzleButton = styled('span')(
  ({ theme }) => {
    const puzzleGlow = keyframes`
      0%, 100% { box-shadow: 0 0 0 0 ${alpha(theme.palette.primary[600], 0)}; }
      50% { box-shadow: 0 0 0 5px ${alpha(theme.palette.primary[600], 0.14)}; }
    `;

    return css`
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 26px;
      height: 26px;
      border-radius: 8px;
      background: ${theme.palette.grey[100]};
      color: ${theme.palette.primary[600]};
      animation: ${puzzleGlow} 2.4s ease-in-out infinite;

      ${REDUCED_MOTION_QUERY} {
        animation: none;
      }
    `;
  },
);

export const SAvatar = styled('span')(
  ({ theme }) => css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: ${theme.palette.primary[600]};
    color: ${theme.palette.common.white};
    font-size: 10px;
    font-weight: 600;
    line-height: 1;
  `,
);

export const SExtensionsMenu = styled('div')(
  ({ theme }) => css`
    position: absolute;
    top: 48px;
    right: 16px;
    width: 224px;
    overflow: hidden;
    border: 1px solid ${theme.palette.grey[200]};
    border-radius: 12px;
    background: ${theme.palette.common.white};
    box-shadow: 0 14px 34px -12px ${alpha(theme.palette.grey[900], 0.4)};
  `,
);

export const SExtensionsHeader = styled('div')(
  ({ theme }) => css`
    padding: 10px 12px 6px;
    color: ${theme.palette.grey[500]};
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
  `,
);

export const SExtensionRow = styled('div', {
  shouldForwardProp: (prop) => prop !== '$active',
})<{ $active?: boolean }>(
  ({ theme, $active = false }) => css`
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 8px 12px;
    background: ${$active ? theme.palette.primary[50] : 'transparent'};
    color: ${theme.palette.primary[600]};
  `,
);

export const SExtensionName = styled('span')(
  ({ theme }) => css`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    color: ${theme.palette.grey[900]};
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
);

export const SPlaceholderIcon = styled('span')(
  ({ theme }) => css`
    flex: none;
    width: 18px;
    height: 18px;
    border-radius: 5px;
    background: ${theme.palette.grey[200]};
  `,
);

export const SPlaceholderLine = styled('span')(
  ({ theme }) => css`
    flex: 1;
    height: 7px;
    border-radius: 999px;
    background: ${theme.palette.grey[100]};
  `,
);

export const SPin = styled('svg')`
  flex: none;
  transform-origin: center;
  animation: ${pinNudge} 2.2s ease-in-out infinite;

  ${REDUCED_MOTION_QUERY} {
    animation: none;
  }
`;

export const SCursor = styled('svg')(
  ({ theme }) => css`
    position: absolute;
    top: 78px;
    right: 22px;
    color: ${theme.palette.grey[900]};
  `,
);

export const SInstalledBadge = styled('span')(
  ({ theme }) => css`
    position: absolute;
    bottom: 12px;
    left: 14px;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 24px;
    padding: 0 10px;
    border-radius: 999px;
    background: ${theme.palette.common.white};
    box-shadow: 0 4px 12px -4px ${alpha(theme.palette.grey[900], 0.28)};
    color: ${theme.palette.grey[800]};
    font-size: 11px;
    font-weight: 600;
    line-height: 1;

    & > svg {
      color: ${theme.palette.success[600]};
    }
  `,
);

export const SDescription = styled('p')(
  ({ theme }) => css`
    margin: 0 0 40px;
    color: ${theme.palette.grey[500]};
    font-size: 14px;
    font-weight: 400;
    line-height: 21px;
    text-wrap: pretty;
  `,
);

export const SCtaButton = styled(Button)(
  ({ theme }) => css`
    gap: 8px;
    height: 48px;
    border-radius: 12px;
    box-shadow: none;
    font-size: 15px;
    font-weight: 600;
    line-height: 20px;

    /* The kit Button wraps its children in a span, so the icon/label gap has to live on that span. */
    & > span {
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }

    &:hover {
      border-color: ${theme.palette.primary[700]};
      background-color: ${theme.palette.primary[700]};
    }
  `,
);
